import type { ExpressionStudy } from "./expression-studies";

export type ExpressionFit = "strong" | "situational" | "high-risk";

const FIT = (direct: ExpressionFit, tactful: ExpressionFit, warm: ExpressionFit, restrained: ExpressionFit, punchy: ExpressionFit) => ({
  direct,
  tactful,
  "warm-authority": warm,
  restrained,
  punchy,
});

/**
 * Scene fit is deliberately separate from authored wording. It describes how
 * safely a stance fits this particular situation, never whether the stance is
 * universally good or bad. The authored trade-off explains the judgment.
 */
export const EXPRESSION_STANCE_FIT: Record<string, Record<string, ExpressionFit>> = {
  "boundary-not-favour": FIT("situational", "strong", "strong", "situational", "high-risk"),
  "disagree-without-a-fight": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "a-no-that-stays-no": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "correct-dont-crush": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "say-what-you-want": FIT("strong", "strong", "strong", "strong", "situational"),
  "the-truth-whole": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "the-exact-feeling": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "move-them-dont-push": FIT("strong", "strong", "strong", "situational", "situational"),
  "sorry-not-smaller": FIT("situational", "strong", "strong", "situational", "high-risk"),
  "cut-to-the-point": FIT("strong", "strong", "situational", "strong", "high-risk"),
  "on-the-record": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "light-and-still-standing": FIT("situational", "strong", "strong", "strong", "high-risk"),
  "finish-before-the-answer": FIT("situational", "strong", "strong", "situational", "high-risk"),
  "correct-the-confident-error": FIT("strong", "strong", "situational", "high-risk", "high-risk"),
  "buy-one-more-day": FIT("strong", "strong", "situational", "situational", "high-risk"),
  "standards-without-threat": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "jealousy-without-ownership": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "ask-for-another-date": FIT("strong", "strong", "situational", "strong", "high-risk"),
  "receive-the-compliment": FIT("strong", "strong", "strong", "situational", "situational"),
  "concede-without-collapsing": FIT("strong", "strong", "situational", "situational", "high-risk"),
  "i-dont-know-yet": FIT("situational", "strong", "strong", "strong", "high-risk"),
  "claim-the-work": FIT("strong", "strong", "strong", "situational", "high-risk"),
  "a-no-that-reads-well": FIT("strong", "strong", "situational", "strong", "high-risk"),
  "say-it-another-way": FIT("strong", "strong", "situational", "strong", "high-risk"),
};

export function expressionStanceFit(studyId: string, stanceId: string): ExpressionFit {
  return EXPRESSION_STANCE_FIT[studyId]?.[stanceId] || "situational";
}

export function validateExpressionStudyFit(studies: ExpressionStudy[]) {
  const errors: string[] = [];
  const expectedIds = new Set(studies.map((study) => study.id));

  for (const study of studies) {
    const fit = EXPRESSION_STANCE_FIT[study.id];
    if (!fit) {
      errors.push(`${study.id}: missing scene-fit judgments`);
      continue;
    }
    for (const stance of study.stances) {
      if (!fit[stance.id]) errors.push(`${study.id}/${stance.id}: missing scene-fit judgment`);
    }
  }
  for (const studyId of Object.keys(EXPRESSION_STANCE_FIT)) {
    if (!expectedIds.has(studyId)) errors.push(`${studyId}: scene-fit judgment has no maintained study`);
  }
  return errors;
}
